/**
 * OTLP/JSON wire types — minimal subset the SDK emits.
 *
 * Spec: https://opentelemetry.io/docs/specs/otlp/  · §OTLP/HTTP, §JSON encoding
 * OTEP-0122: https://github.com/open-telemetry/oteps/blob/main/text/0122-otlp-http-json.md
 *
 * The OTLP/JSON encoding rules we obey:
 *   - All keys in `lowerCamelCase` (not snake_case).
 *   - `traceId` / `spanId` are LOWERCASE hex strings (16 / 32 chars resp.).
 *   - Enum values (severityNumber, spanKind, statusCode) are INTEGERS.
 *   - Timestamps are nanoseconds-since-epoch encoded as STRINGS
 *     (JavaScript numbers can't represent uint64 ns precision).
 *
 * The SDK never imports `@opentelemetry/api` at runtime — these types are
 * the entire contract with the wire. Bundler-friendly, RN-safe, no Node-only
 * `crypto`/`fs` deps.
 */

// ─── KeyValue + AnyValue ────────────────────────────────────────────────────

export interface OtlpAnyValue {
  readonly stringValue?: string;
  readonly intValue?: string; // int64 as string per OTEP-0122
  readonly doubleValue?: number;
  readonly boolValue?: boolean;
  readonly arrayValue?: { readonly values: ReadonlyArray<OtlpAnyValue> };
  readonly kvlistValue?: { readonly values: ReadonlyArray<OtlpKeyValue> };
}

export interface OtlpKeyValue {
  readonly key: string;
  readonly value: OtlpAnyValue;
}

// ─── Resource ────────────────────────────────────────────────────────────────

export interface OtlpResource {
  readonly attributes: ReadonlyArray<OtlpKeyValue>;
  readonly droppedAttributesCount?: number;
}

// ─── Instrumentation scope ───────────────────────────────────────────────────

export interface OtlpInstrumentationScope {
  readonly name: string;
  readonly version?: string;
  readonly attributes?: ReadonlyArray<OtlpKeyValue>;
}

// ─── Logs ────────────────────────────────────────────────────────────────────

/**
 * Severity numbers per OTel logs spec. Values are deliberately not contiguous
 * (gaps reserved for sub-levels).
 *
 * https://opentelemetry.io/docs/specs/otel/logs/data-model/#field-severitynumber
 */
export const SeverityNumber = {
  UNSPECIFIED: 0,
  TRACE: 1,
  DEBUG: 5,
  INFO: 9,
  WARN: 13,
  ERROR: 17,
  FATAL: 21,
} as const;
export type SeverityNumber = (typeof SeverityNumber)[keyof typeof SeverityNumber];

export interface OtlpLogRecord {
  readonly timeUnixNano: string;
  readonly observedTimeUnixNano?: string;
  readonly severityNumber: SeverityNumber;
  readonly severityText: string;
  readonly body: OtlpAnyValue;
  readonly attributes: ReadonlyArray<OtlpKeyValue>;
  readonly traceId?: string;
  readonly spanId?: string;
}

export interface OtlpScopeLogs {
  readonly scope: OtlpInstrumentationScope;
  readonly logRecords: ReadonlyArray<OtlpLogRecord>;
}

export interface OtlpResourceLogs {
  readonly resource: OtlpResource;
  readonly scopeLogs: ReadonlyArray<OtlpScopeLogs>;
}

export interface OtlpExportLogsServiceRequest {
  readonly resourceLogs: ReadonlyArray<OtlpResourceLogs>;
}

// ─── Traces ──────────────────────────────────────────────────────────────────

export const SpanKind = {
  UNSPECIFIED: 0,
  INTERNAL: 1,
  SERVER: 2,
  CLIENT: 3,
  PRODUCER: 4,
  CONSUMER: 5,
} as const;
export type SpanKind = (typeof SpanKind)[keyof typeof SpanKind];

export const StatusCode = {
  UNSET: 0,
  OK: 1,
  ERROR: 2,
} as const;
export type StatusCode = (typeof StatusCode)[keyof typeof StatusCode];

export interface OtlpSpanStatus {
  readonly code: StatusCode;
  readonly message?: string;
}

export interface OtlpSpanEvent {
  readonly timeUnixNano: string;
  readonly name: string;
  readonly attributes?: ReadonlyArray<OtlpKeyValue>;
}

export interface OtlpSpan {
  readonly traceId: string; // 32 lowercase hex chars
  readonly spanId: string; // 16 lowercase hex chars
  readonly parentSpanId?: string;
  readonly name: string;
  readonly kind: SpanKind;
  readonly startTimeUnixNano: string;
  readonly endTimeUnixNano: string;
  readonly attributes: ReadonlyArray<OtlpKeyValue>;
  readonly events?: ReadonlyArray<OtlpSpanEvent>;
  readonly status?: OtlpSpanStatus;
}

export interface OtlpScopeSpans {
  readonly scope: OtlpInstrumentationScope;
  readonly spans: ReadonlyArray<OtlpSpan>;
}

export interface OtlpResourceSpans {
  readonly resource: OtlpResource;
  readonly scopeSpans: ReadonlyArray<OtlpScopeSpans>;
}

export interface OtlpExportTraceServiceRequest {
  readonly resourceSpans: ReadonlyArray<OtlpResourceSpans>;
}

// ─── Metrics ─────────────────────────────────────────────────────────────────

/**
 * Aggregation temporality. Cumulative = monotonic-since-start; Delta = per
 * collection interval. We use CUMULATIVE for counters (OTel best-practice
 * for HTTP-backed exporters) and DELTA for histograms (cheaper for short
 * sessions).
 */
export const AggregationTemporality = {
  UNSPECIFIED: 0,
  DELTA: 1,
  CUMULATIVE: 2,
} as const;
export type AggregationTemporality =
  (typeof AggregationTemporality)[keyof typeof AggregationTemporality];

export interface OtlpNumberDataPoint {
  readonly attributes: ReadonlyArray<OtlpKeyValue>;
  readonly startTimeUnixNano: string;
  readonly timeUnixNano: string;
  readonly asInt?: string;
  readonly asDouble?: number;
}

export interface OtlpHistogramDataPoint {
  readonly attributes: ReadonlyArray<OtlpKeyValue>;
  readonly startTimeUnixNano: string;
  readonly timeUnixNano: string;
  readonly count: string;
  readonly sum: number;
  readonly bucketCounts: ReadonlyArray<string>;
  readonly explicitBounds: ReadonlyArray<number>;
  readonly min?: number;
  readonly max?: number;
}

export interface OtlpSum {
  readonly dataPoints: ReadonlyArray<OtlpNumberDataPoint>;
  readonly aggregationTemporality: AggregationTemporality;
  readonly isMonotonic: boolean;
}

export interface OtlpGauge {
  readonly dataPoints: ReadonlyArray<OtlpNumberDataPoint>;
}

export interface OtlpHistogram {
  readonly dataPoints: ReadonlyArray<OtlpHistogramDataPoint>;
  readonly aggregationTemporality: AggregationTemporality;
}

export interface OtlpMetric {
  readonly name: string;
  readonly description?: string;
  readonly unit?: string;
  readonly sum?: OtlpSum;
  readonly gauge?: OtlpGauge;
  readonly histogram?: OtlpHistogram;
}

export interface OtlpScopeMetrics {
  readonly scope: OtlpInstrumentationScope;
  readonly metrics: ReadonlyArray<OtlpMetric>;
}

export interface OtlpResourceMetrics {
  readonly resource: OtlpResource;
  readonly scopeMetrics: ReadonlyArray<OtlpScopeMetrics>;
}

export interface OtlpExportMetricsServiceRequest {
  readonly resourceMetrics: ReadonlyArray<OtlpResourceMetrics>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Build an OtlpKeyValue from a primitive. JSON-safe primitives are sufficient
 * for every attribute the SDK emits.
 */
export function kv(key: string, value: string | number | boolean): OtlpKeyValue {
  if (typeof value === 'string') return { key, value: { stringValue: value } };
  if (typeof value === 'boolean') return { key, value: { boolValue: value } };
  // number — choose int representation when safe, otherwise double.
  if (Number.isInteger(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER) {
    return { key, value: { intValue: String(value) } };
  }
  return { key, value: { doubleValue: value } };
}

/** Build an attribute list from a flat record. Drops undefined values. */
export function attrs(
  record: Readonly<Record<string, string | number | boolean | undefined>>,
): ReadonlyArray<OtlpKeyValue> {
  const out: OtlpKeyValue[] = [];
  for (const [k, v] of Object.entries(record)) {
    if (v === undefined) continue;
    out.push(kv(k, v));
  }
  return out;
}

/** Wall-clock ms → ns-since-epoch string (uint64). */
export function nowNs(now: () => number = Date.now): string {
  return msToNs(now());
}

/**
 * Convert ms (float, sub-ms precision from `performance.now()` welcome) to
 * an ns-since-epoch string. Uses string concatenation so the result is exact
 * for whole-millisecond inputs (no float rounding past 2^53 in 2255).
 */
export function msToNs(ms: number): string {
  const whole = Math.trunc(ms);
  const fracMs = ms - whole; // 0 .. 0.999…
  const fracNs = Math.max(0, Math.min(999_999, Math.round(fracMs * 1_000_000)));
  const fracPadded = String(fracNs).padStart(6, '0');
  return `${whole}${fracPadded}`;
}

/** Hex-encode a Uint8Array as lowercase OTel-spec trace/span id. */
export function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i] ?? 0;
    s += (v < 16 ? '0' : '') + v.toString(16);
  }
  return s;
}

/**
 * Generate a random trace id (16 bytes → 32 hex). Uses WebCrypto when
 * available (`crypto.getRandomValues`) and falls back to Math.random in
 * cold-test environments. Trace ids are not a security boundary in this
 * SDK — they're correlation keys.
 */
export function newTraceId(): string {
  return bytesToHex(randomBytes(16));
}

export function newSpanId(): string {
  return bytesToHex(randomBytes(8));
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  const g = globalThis as {
    crypto?: { getRandomValues?: (b: Uint8Array) => Uint8Array };
  };
  if (g.crypto?.getRandomValues) {
    g.crypto.getRandomValues(out);
    return out;
  }
  for (let i = 0; i < n; i++) out[i] = Math.floor(Math.random() * 256);
  return out;
}
